import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Check, Sparkles, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";

const PACK_META = {
  coins_mini: { Icon: Sparkles, blurbKey: "pricing.blurbMini" },
  coins_pack: { Icon: Zap,      blurbKey: "pricing.blurbPack", highlight: true },
};

export default function Pricing() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/packs");
        setPacks(data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const buy = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pricing-page">
      <div className="glass-header sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 h-20">
          <button onClick={() => nav("/")} className="btn-ghost focus-ring" data-testid="pricing-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.15em]">{t("pricing.back")}</span>
          </button>
          <div className="text-right leading-tight">
            <div className="label-eyebrow">{t("pricing.currentBalance")}</div>
            <div className="font-mono-p text-[13px] text-white/90" data-testid="pricing-credit-balance">
              {user?.credits ?? 0} {t("nav.coins")}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="label-eyebrow mb-3">{t("pricing.eyebrow")}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-4">
          {t("pricing.title1")}<br />
          <span className="text-white/50">{t("pricing.title2")}</span>
        </h1>
        <p className="text-white/60 text-[15px] max-w-lg mb-14">
          {t("pricing.subtitle")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {packs.map((p) => {
            const meta = PACK_META[p.lookup_key] || {};
            const Icon = meta.Icon || Sparkles;
            const highlight = meta.highlight;
            const perCoin = (p.price_usd / p.credits).toFixed(3);
            return (
              <div
                key={p.lookup_key}
                className={`relative p-7 rounded-lg border ${
                  highlight
                    ? "border-white/30 bg-white/[0.03]"
                    : "border-white/10 bg-black/40"
                } flex flex-col`}
                data-testid={`pack-${p.lookup_key}`}
              >
                {highlight && (
                  <div
                    className="absolute -top-3 left-6 label-eyebrow px-2 py-1 rounded bg-white"
                    style={{ color: "#0A0A0A" }}
                  >
                    {t("pricing.bestValue")}
                  </div>
                )}
                <div className="flex items-center justify-between mb-6">
                  <Icon className="w-6 h-6 text-white/70" />
                  <div className="label-eyebrow">{p.label}</div>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-display text-5xl font-black tracking-tighter">
                    ${Number.isInteger(p.price_usd) ? p.price_usd.toFixed(0) : p.price_usd.toFixed(2)}
                  </span>
                </div>
                <div className="font-mono-p text-[12px] text-white/60 mb-6">
                  {t("pricing.perCoin", { amount: perCoin })}
                </div>

                <ul className="space-y-2.5 mb-8 text-[13px] text-white/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-white/50" />
                    <span>{t("pricing.feature1", { count: p.credits.toLocaleString() })}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-white/50" />
                    <span>{t("pricing.feature2")}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-white/50" />
                    <span>{meta.blurbKey ? t(meta.blurbKey) : t("pricing.neverExpire")}</span>
                  </li>
                </ul>

                <button
                  onClick={() => buy(p.lookup_key)}
                  disabled={loadingKey === p.lookup_key}
                  className={`${highlight ? "btn-pill" : "btn-ghost"} justify-center h-11 focus-ring w-full`}
                  data-testid={`buy-${p.lookup_key}`}
                >
                  {loadingKey === p.lookup_key ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>{t("pricing.buy", { label: p.label })}</>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-14 text-center label-eyebrow opacity-60">
          {t("pricing.footer")}
        </div>
        <div className="mt-4 text-center text-white/40 text-[12px]">
          <Link to="/history/transactions" className="hover:text-white underline" data-testid="link-transactions">
            {t("pricing.viewPast")}
          </Link>
        </div>
      </div>
    </div>
  );
}
